'use client';

import React, { useCallback, useEffect, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useSignUp, useAuth } from '@clerk/nextjs';
import type { OAuthStrategy } from '@clerk/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useToast } from '@/components/ui/use-toast';
import {
  Eye,
  EyeOff,
  Mail,
  Lock,
  User as UserIcon,
  UserPlus,
  AlertCircle,
  RefreshCw,
} from 'lucide-react';
import { SOCIAL_PROVIDERS, type SupportedProvider } from '@/constants/social-auth';

type Step = 'form' | 'verify';

const PROVIDER_STRATEGY: Record<SupportedProvider, OAuthStrategy> = {
  google: 'oauth_google',
  facebook: 'oauth_facebook',
};

const SignupPage = () => {
  const router = useRouter();
  const { toast } = useToast();
  const { isSignedIn, isLoaded: authLoaded } = useAuth();
  const { isLoaded, signUp, setActive } = useSignUp();

  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [signupError, setSignupError] = useState<string | null>(null);
  const [step, setStep] = useState<Step>('form');
  const [otp, setOtp] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', password: '', creatorType: '' });

  useEffect(() => {
    if (authLoaded && isSignedIn) {
      router.replace('/profile/builder?tab=basic');
    }
  }, [authLoaded, isSignedIn, router]);

  const handleSignup = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLoaded) return;
    setIsLoading(true);
    setSignupError(null);
    try {
      const [firstName, ...rest] = formData.name.trim().split(' ');
      await signUp.create({
        emailAddress: formData.email.trim(),
        password: formData.password,
        firstName: firstName || undefined,
        lastName: rest.length ? rest.join(' ') : undefined,
        // Clerk forwards unsafeMetadata onto the created user, where our
        // webhook (convex/http.ts) can read it via data.unsafe_metadata
        // and thread the creatorType into the Convex profile.
        unsafeMetadata: formData.creatorType
          ? { creatorType: formData.creatorType }
          : undefined,
      });
      await signUp.prepareEmailAddressVerification({ strategy: 'email_code' });
      setStep('verify');
    } catch (err: any) {
      const msg = err?.errors?.[0]?.longMessage ?? err?.errors?.[0]?.message ?? 'Sign-up failed';
      setSignupError(msg);
    } finally {
      setIsLoading(false);
    }
  }, [isLoaded, signUp, formData]);

  const handleVerifyOtp = useCallback(async () => {
    if (!isLoaded || otp.trim().length < 6) return;
    setIsVerifying(true);
    setSignupError(null);
    try {
      const result = await signUp.attemptEmailAddressVerification({ code: otp.trim() });
      if (result.status === 'complete') {
        await setActive({ session: result.createdSessionId });
        router.push('/profile/builder?tab=basic');
      } else {
        setSignupError('Verification incomplete. Please retry.');
      }
    } catch (err: any) {
      const msg = err?.errors?.[0]?.longMessage ?? err?.errors?.[0]?.message ?? 'Invalid code';
      setSignupError(msg);
    } finally {
      setIsVerifying(false);
    }
  }, [isLoaded, signUp, setActive, otp, router]);

  const handleResendCode = useCallback(async () => {
    if (!isLoaded) return;
    try {
      await signUp.prepareEmailAddressVerification({ strategy: 'email_code' });
      toast({ title: 'Code resent', description: 'Check your inbox for a new code.' });
    } catch (err: any) {
      const msg = err?.errors?.[0]?.longMessage ?? err?.errors?.[0]?.message ?? 'Resend failed';
      toast({ title: 'Resend failed', description: msg, variant: 'destructive' });
    }
  }, [isLoaded, signUp, toast]);

  const handleSocialSignup = useCallback(async (provider: SupportedProvider) => {
    if (!isLoaded) return;
    setIsLoading(true);
    try {
      await signUp.authenticateWithRedirect({
        strategy: PROVIDER_STRATEGY[provider],
        redirectUrl: '/sso-callback',
        redirectUrlComplete: '/profile/builder?tab=basic',
      });
    } catch (err: any) {
      const msg = err?.errors?.[0]?.longMessage ?? err?.errors?.[0]?.message ?? 'OAuth failed';
      toast({ title: 'Social signup failed', description: msg, variant: 'destructive' });
      setIsLoading(false);
    }
  }, [isLoaded, signUp, toast]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background px-4 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <img src="/assets/brand/Outhere_logo_black.png" alt="OutThere" className="inline-block h-[125px] w-[250px] object-contain dark:hidden" />
          <img src="/assets/brand/Outhere_logo_white.png" alt="OutThere" className="hidden dark:inline-block h-[125px] w-[250px] object-contain" />
          <p className="text-muted-foreground mt-2">Create your creator account</p>
        </div>

        <Card>
          {step === 'verify' ? (
            <>
              <CardHeader>
                <CardTitle>Verify your email</CardTitle>
                <CardDescription>
                  We sent a 6-digit code to <strong>{formData.email}</strong>
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="otp">Verification code</Label>
                  <Input
                    id="otp"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="123456"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    autoFocus
                  />
                </div>
                {signupError && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{signupError}</AlertDescription>
                  </Alert>
                )}
                <Button onClick={handleVerifyOtp} disabled={isVerifying || otp.length < 6} className="w-full bg-red-500 hover:bg-red-600">
                  {isVerifying ? (
                    <><RefreshCw className="mr-2 h-4 w-4 animate-spin" />Verifying…</>
                  ) : (
                    <>Verify & continue</>
                  )}
                </Button>
                <div className="flex items-center justify-between text-sm">
                  <button type="button" onClick={handleResendCode} className="text-blue-600 hover:text-blue-800">
                    Resend code
                  </button>
                  <button type="button" onClick={() => { setStep('form'); setOtp(''); setSignupError(null); }} className="text-muted-foreground hover:text-foreground">
                    Back
                  </button>
                </div>
              </CardContent>
            </>
          ) : (
            <Tabs defaultValue="email">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="email">Email</TabsTrigger>
                <TabsTrigger value="social">Social</TabsTrigger>
              </TabsList>
              <TabsContent value="email">
                <CardHeader>
                  <CardTitle>Create your account</CardTitle>
                  <CardDescription>Start sharing what you do</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSignup} className="space-y-4">
                    {signupError && (
                      <Alert variant="destructive">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>{signupError}</AlertDescription>
                      </Alert>
                    )}
                    <div className="space-y-2">
                      <Label htmlFor="name">Name</Label>
                      <div className="relative">
                        <UserIcon className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="name"
                          value={formData.name}
                          onChange={(e) => { setFormData((p) => ({ ...p, name: e.target.value })); if (signupError) setSignupError(null); }}
                          placeholder="Your name"
                          autoComplete="name"
                          className="pl-10"
                          required
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => { setFormData((p) => ({ ...p, email: e.target.value })); if (signupError) setSignupError(null); }}
                          placeholder="you@example.com"
                          autoComplete="email"
                          className="pl-10"
                          required
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="password">Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="password"
                          type={showPassword ? 'text' : 'password'}
                          value={formData.password}
                          onChange={(e) => { setFormData((p) => ({ ...p, password: e.target.value })); if (signupError) setSignupError(null); }}
                          placeholder="At least 8 characters"
                          autoComplete="new-password"
                          className="pl-10"
                          required
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute right-1 top-1 h-8 w-8 p-0"
                          onClick={() => setShowPassword((s) => !s)}
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </Button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="creatorType">Creator Type (Optional)</Label>
                      <select
                        id="creatorType"
                        name="creatorType"
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        value={formData.creatorType}
                        onChange={(e) => setFormData((p) => ({ ...p, creatorType: e.target.value }))}
                      >
                        <option value="">Select creator type</option>
                        <option value="video">Video Creator</option>
                        <option value="writer">Writer/Blogger</option>
                        <option value="artist">Artist/Designer</option>
                        <option value="musician">Musician</option>
                        <option value="podcaster">Podcaster</option>
                        <option value="photographer">Photographer</option>
                        <option value="gamer">Gamer</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      By signing up, you agree to our{' '}
                      <Link href="/terms" className="text-blue-600 hover:text-blue-800">Terms</Link> and{' '}
                      <Link href="/privacy" className="text-blue-600 hover:text-blue-800">Privacy Policy</Link>.
                    </p>
                    <div id="clerk-captcha" />
                    <Button type="submit" disabled={isLoading || !isLoaded} className="w-full bg-red-500 hover:bg-red-600">
                      {isLoading ? (
                        <><RefreshCw className="mr-2 h-4 w-4 animate-spin" />Creating account…</>
                      ) : (
                        <><UserPlus className="mr-2 h-4 w-4" />Create account</>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </TabsContent>
              <TabsContent value="social">
                <CardHeader>
                  <CardTitle>Sign up with social</CardTitle>
                  <CardDescription>Use Google or Facebook</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {SOCIAL_PROVIDERS.map(({ provider, label, icon: Icon }) => (
                    <Button
                      key={provider}
                      variant="outline"
                      className="relative w-full flex items-center justify-center px-8"
                      onClick={() => handleSocialSignup(provider)}
                      disabled={isLoading || !isLoaded}
                    >
                      <Icon className="absolute left-6 top-1/2 -translate-y-1/2" />
                      <span className="w-full text-center leading-5">{label}</span>
                    </Button>
                  ))}
                </CardContent>
              </TabsContent>
            </Tabs>
          )}

          {step === 'form' && (
            <CardFooter className="flex flex-col items-center justify-center space-y-2 pt-0">
              <div className="text-sm text-muted-foreground">
                Already have an account?{' '}
                <Link href="/auth/login" className="text-blue-600 hover:text-blue-800 font-medium">
                  Sign in
                </Link>
              </div>
            </CardFooter>
          )}
        </Card>

        <p className="mt-8 text-center text-sm text-muted-foreground">
          © {new Date().getFullYear()} OutThere. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default function Page() {
  return <SignupPage />;
}
